import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { Tables } from '@limo/database'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { CurrencyInput } from '@/components/ui/currency-input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { Loader2 } from 'lucide-react'

type CustomFee = Tables<'custom_fees'>

const customFeeSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name too long'),
  fee_type: z.enum(['flat', 'percentage']),
  amount: z.coerce.number().min(0, 'Must be 0 or greater'),
  is_taxable: z.boolean().default(false),
  is_active: z.boolean().default(true),
  sort_order: z.coerce.number().int().min(0).default(0),
})

type CustomFeeFormData = z.infer<typeof customFeeSchema>

interface CustomFeeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  fee?: CustomFee | null
  accountId: string
  onSuccess?: () => void
}

export function CustomFeeDialog({
  open,
  onOpenChange,
  fee,
  accountId,
  onSuccess,
}: CustomFeeDialogProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const isEditing = !!fee

  const form = useForm<CustomFeeFormData>({
    resolver: zodResolver(customFeeSchema),
    defaultValues: {
      name: '',
      fee_type: 'flat',
      amount: 0,
      is_taxable: false,
      is_active: true,
      sort_order: 0,
    },
  })

  const feeType = form.watch('fee_type')

  useEffect(() => {
    if (open) {
      if (fee) {
        form.reset({
          name: fee.name,
          fee_type: fee.fee_type as 'flat' | 'percentage',
          amount: Number(fee.amount),
          is_taxable: fee.is_taxable,
          is_active: fee.is_active,
          sort_order: fee.sort_order,
        })
      } else {
        form.reset({
          name: '',
          fee_type: 'flat',
          amount: 0,
          is_taxable: false,
          is_active: true,
          sort_order: 0,
        })
      }
    }
  }, [open, fee, form])

  const mutation = useMutation({
    mutationFn: async (data: CustomFeeFormData) => {
      const payload = {
        account_id: accountId,
        name: data.name,
        fee_type: data.fee_type,
        amount: data.amount,
        is_taxable: data.is_taxable,
        is_active: data.is_active,
        sort_order: data.sort_order,
      }

      if (isEditing && fee) {
        const { error } = await supabase
          .from('custom_fees')
          .update(payload)
          .eq('id', fee.id)
        if (error) throw error
      } else {
        const { error } = await supabase.from('custom_fees').insert(payload)
        if (error) throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-fees', accountId] })
      toast({
        title: isEditing ? 'Fee updated' : 'Fee created',
        description: `Custom fee "${form.getValues('name')}" has been ${isEditing ? 'updated' : 'created'}.`,
      })
      onOpenChange(false)
      onSuccess?.()
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Custom Fee' : 'Add Custom Fee'}
          </DialogTitle>
          <DialogDescription>
            Create a custom fee that will be applied to all bookings for this account.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((data) => mutation.mutate(data))}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Credit Card Fee" {...field} />
                  </FormControl>
                  <FormDescription>
                    This label will be shown to customers in the pricing breakdown
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="fee_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Fee Type</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select fee type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="flat">Flat Amount ($)</SelectItem>
                      <SelectItem value="percentage">Percentage (%)</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Amount</FormLabel>
                  <FormControl>
                    {feeType === 'flat' ? (
                      <CurrencyInput
                        value={field.value}
                        onValueChange={(val) => field.onChange(val ?? 0)}
                      />
                    ) : (
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          step={0.1}
                          className="w-24"
                          {...field}
                        />
                        <span className="text-muted-foreground">%</span>
                      </div>
                    )}
                  </FormControl>
                  <FormDescription>
                    {feeType === 'flat'
                      ? 'Fixed dollar amount added to every booking'
                      : 'Percentage of the booking subtotal'}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_taxable"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>Include in Tax Base</FormLabel>
                    <FormDescription className="text-xs">
                      When enabled, this fee is added to the taxable subtotal before tax is calculated
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>Active</FormLabel>
                    <FormDescription className="text-xs">
                      Enable this fee for all bookings
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="sort_order"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sort Order</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={0}
                      className="w-24"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Lower numbers appear first in the pricing breakdown
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {isEditing ? 'Save Changes' : 'Create Fee'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
